import CheckIcon from '@mui/icons-material/Check';
import { Box, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { vehicleFormHooks } from 'app-components/flux/vehicle/vehicleForm';
import type { VehicleInputMode } from 'app-components/flux/vehicle/vehicleTypes';
import { tracker } from 'app-components/utils/core';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { MakeInput, MilesInput, ModelInput, TrimInput, VinInput, YearInput, ZipCodeInput } from '../VehicleFields';
import { UncheckedIcon } from '../utils';

const useStyles = makeStyles()((theme) => ({
  mainTitle: {
    marginBottom: theme.spacing(2),
  },
  inputModeOptions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    border: `1px solid ${theme.palette.border.light}`,
    borderRadius: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,

    '& >:first-child': {
      borderRight: `1px solid ${theme.palette.border.light}`,
    },
  },
  inputOption: {
    flex: 1,
    margin: 0,
    padding: theme.spacing(2, 3, 2, 3),
  },
  selectedInputOption: {
    color: theme.palette.active.main,
  },
  radioRoot: {
    height: 20,
    width: 20,
    padding: 0,
    marginRight: theme.spacing(1.25),

    '&.Mui-checked': {
      color: theme.palette.background.paper,
      backgroundColor: theme.palette.active.main,
      '& svg': {
        padding: theme.spacing(0.5),
      },
    },
  },
  uncheckedIcon: {
    color: theme.palette.border.dark,
  },
  makeModelInputContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(0.5, 3),
    padding: theme.spacing(3, 0, 3, 0),
  },
  vinInputContainer: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(3),
    gap: theme.spacing(0.5),
  },
  milesZipcodeContainer: {
    display: 'flex',
    gap: theme.spacing(3),
  },
  vinHelp: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
}));

export const DetailsStep = () => {
  const { classes } = useStyles();

  const manager = vehicleFormHooks.useManager();
  const { value: inputMode } = vehicleFormHooks.useField('inputMode');

  if (inputMode === 'makeModel') {
    tracker.track(tracker.events.kbbStart, { userAction: 'selectMakeModel' });
  } else if (inputMode === 'vin') {
    tracker.track(tracker.events.kbbStart, { userAction: 'useVin' });
  }

  const switchInputMode = (mode: VehicleInputMode) => {
    manager.reinit(manager.runSelector(({ state }) => ({ ...state, inputMode: mode })));
  };

  return (
    <Box>
      <Box>
        <Typography variant='headlineRegular' className={classes.mainTitle}>
          Let&apos;s find the value of your car
        </Typography>
        <FormControl fullWidth>
          <RadioGroup
            className={classes.inputModeOptions}
            value={inputMode}
            onChange={(e) => switchInputMode(e.target.value as VehicleInputMode)}
          >
            <FormControlLabel
              value='makeModel'
              className={`${classes.inputOption} ${inputMode === 'makeModel' ? classes.selectedInputOption : ''}`}
              control={
                <Radio
                  icon={<UncheckedIcon className={classes.uncheckedIcon} />}
                  checkedIcon={<CheckIcon />}
                  className={classes.radioRoot}
                />
              }
              label={'Use Make/Model'}
            />
            <FormControlLabel
              value='vin'
              className={`${classes.inputOption} ${inputMode === 'vin' ? classes.selectedInputOption : ''}`}
              control={
                <Radio
                  icon={<UncheckedIcon className={classes.uncheckedIcon} />}
                  checkedIcon={<CheckIcon />}
                  className={classes.radioRoot}
                />
              }
              label={'Use VIN'}
            />
          </RadioGroup>
        </FormControl>
      </Box>
      {inputMode === 'makeModel' ? (
        <Box className={classes.makeModelInputContainer}>
          <YearInput />
          <MakeInput />
          <ModelInput />
          <TrimInput />
          <MilesInput />
          <ZipCodeInput />
        </Box>
      ) : (
        <Box className={classes.vinInputContainer}>
          <VinInput />
          <Box className={classes.milesZipcodeContainer}>
            <MilesInput />
            <ZipCodeInput />
          </Box>
          <Box className={classes.vinHelp}>
            <Typography variant='paragraphMedium'>Where&apos;s my VIN?</Typography>
            <Typography variant='paragraphRegular' lineHeight='normal'>
              Your VIN always have 17 characters and can be found on the driver&apos;s doorjamb, driver side dashboard,
              vehicle registration card, or insurance card.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};
